import { useState } from 'react';
import { getApiV1VolunteersUserIdDocuments } from '@/shared/api/generated/volunteers/volunteers';
import { useLocale } from '@/shared/lib/i18n/locale-context';

/**
 * Opens a volunteer's document for review (§23.8).
 *
 * ⚠️ The link is fetched at the moment of the click, never with the row.
 *
 * Three reasons, and the first is the important one:
 * 1. Every issued link is an audited read of a person's identity document (§12.7). Fetching links
 *    for a page of twenty rows would record twenty reads of twenty volunteers' papers because
 *    somebody opened a list — and the audit log would then be unable to show who was actually looked
 *    at.
 * 2. The URL expires in minutes; one fetched with the page is stale before it is clicked.
 * 3. A URL in a rendered list is a bearer credential sitting in the DOM.
 */
export function DocumentLink({ userId, kind }: { userId: string; kind: 'id' | 'certificate' }) {
  const { t } = useLocale();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = async () => {
    setBusy(true);
    setError(null);

    try {
      const documents = await getApiV1VolunteersUserIdDocuments(userId);
      const url = kind === 'id' ? documents.idDocumentUrl : documents.certificateUrl;

      if (!url) {
        setError(t.volunteers.documentUnavailable);
        return;
      }

      // `noopener` is not optional. The document is served from the storage origin, and without it
      // the opened tab keeps a handle on `window.opener` — a reference to an authenticated admin
      // session, handed to a page whose contents were uploaded by a volunteer.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError(t.volunteers.documentUnavailable);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => void open()}
        disabled={busy}
        title={t.volunteers.documentAuditNotice}
        className="text-start text-success underline underline-offset-4 disabled:opacity-60"
      >
        {busy
          ? t.volunteers.openingDocument
          : kind === 'id'
            ? t.volunteers.openDocument
            : t.volunteers.openCertificate}
      </button>

      {error ? <span className="text-xs text-danger">{error}</span> : null}
    </div>
  );
}
